import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Landmark, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  useCashflowProjection,
  useCreateScheduledPayment,
  useDeleteScheduledPayment,
  useScheduledPayments,
  useUpdateScheduledPayment,
} from '../hooks/useCashflow';
import { useUpdateBankAccount } from '../hooks/useBankAccounts';
import type {
  AccountBalance,
  BankBalances,
  CashflowHorizon,
  CashflowItem,
  ScheduledPayment,
  ScheduledPaymentInput,
} from '../lib/cashflowApi';
import { buildCashflowDays } from '../lib/cashflowDays';
import { CompanyMultiSelect } from '../components/financials/CompanyMultiSelect';
import { ScheduledPaymentFormModal } from '../components/financials/ScheduledPaymentFormModal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { FadeIn, SkeletonChart, SkeletonTable } from '../components/ui/Skeleton';
import { IncomeExpenseBarChart } from '../components/dashboard/charts';
import { usePermissions } from '../hooks/usePermissions';
import { useActiveCompanyId } from '../store/useCompanyStore';
import { useI18n } from '../contexts/I18nContext';

const HORIZONS: CashflowHorizon[] = [1, 2, 3, 6];

/** Amount with cents (formatCurrency in lib/format rounds to whole units). */
function fmtAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('ro-RO', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Unknown/custom currency codes fall back to a plain suffix.
    return `${amount.toLocaleString('ro-RO')} ${currency}`;
  }
}

function fmtDate(iso: string): string {
  return new Intl.DateTimeFormat('ro-RO', { dateStyle: 'medium', timeZone: 'UTC' }).format(
    new Date(iso),
  );
}

/**
 * Cash flow submodule (Financiar › Plăți & Încasări viitoare): projects
 * upcoming money in/out over a 1/2/3/6-month horizon from monthly
 * subscriptions, planned payments/receipts and unpaid invoices. No FX —
 * everything is grouped strictly per currency.
 */
export function CashflowPage() {
  const { t, locale } = useI18n();
  const { can } = usePermissions();
  const canManage = can('financials', 'canManageScheduledPayments');
  // Manual balance override rides on the bank-accounts CRUD permission.
  const canEditBalances = can('settings', 'canManageCompanies');

  const [months, setMonths] = useState<CashflowHorizon>(3);
  const activeCompanyId = useActiveCompanyId();
  const [companyIds, setCompanyIds] = useState<string[]>(activeCompanyId ? [activeCompanyId] : []);
  const scopedCompanyIds = companyIds.length > 0 ? companyIds : undefined;

  const projection = useCashflowProjection(months, scopedCompanyIds);
  // PLANNED rows power the edit/delete actions on 'planned' projection items.
  const plannedList = useScheduledPayments({ status: 'PLANNED', limit: 100 });
  const plannedById = useMemo(() => {
    const map = new Map<string, ScheduledPayment>();
    for (const sp of plannedList.data?.data ?? []) map.set(sp.id, sp);
    return map;
  }, [plannedList.data]);

  const createMutation = useCreateScheduledPayment();
  const updateMutation = useUpdateScheduledPayment();
  const deleteMutation = useDeleteScheduledPayment();

  const [modal, setModal] = useState<
    { mode: 'create' } | { mode: 'edit'; row: ScheduledPayment } | null
  >(null);
  const [deleting, setDeleting] = useState<ScheduledPayment | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Expanded month buckets in the monthly table (current month open by default).
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const data = projection.data;
  const isExpanded = (month: string, index: number) =>
    expanded.has(month) || (expanded.size === 0 && index === 0);
  const toggleExpanded = (month: string, index: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.size === 0 && index !== 0) next.add(data?.buckets[0]?.month ?? '');
      if (isExpanded(month, index)) next.delete(month);
      else next.add(month);
      return next;
    });
  };

  // One currency at a time (no FX): the header toggle picks which one the
  // chart, the monthly table and the lists show.
  const currencies = useMemo(
    () => (data ? data.totalsByCurrency.map((c) => c.currency) : []),
    [data],
  );
  const [currency, setCurrency] = useState<string | null>(null);
  const activeCurrency =
    currency && currencies.includes(currency) ? currency : (currencies[0] ?? 'RON');

  const monthLabel = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale === 'ro' ? 'ro-RO' : 'en-US', {
      month: 'long',
      year: 'numeric',
    });
    return (month: string) => {
      const [y, m] = month.split('-').map(Number);
      return fmt.format(new Date(y, m - 1, 1));
    };
  }, [locale]);

  const chartData = useMemo(
    () =>
      (data?.buckets ?? []).map((b) => {
        const totals = b.byCurrency.find((c) => c.currency === activeCurrency);
        return {
          label: monthLabel(b.month),
          income: totals?.inflow ?? 0,
          expenses: totals?.outflow ?? 0,
        };
      }),
    [data, activeCurrency, monthLabel],
  );

  // The whole page shows one currency at a time (no FX, ever): buckets, item
  // lists and projected balances are narrowed to the active currency.
  const visibleBuckets = useMemo(
    () =>
      (data?.buckets ?? []).map((b) => ({
        ...b,
        byCurrency: b.byCurrency.filter((c) => c.currency === activeCurrency),
        items: b.items.filter((i) => i.currency === activeCurrency),
        projectedBalance: (b.projectedBalance ?? []).filter((pb) => pb.currency === activeCurrency),
      })),
    [data, activeCurrency],
  );

  const allItems = useMemo(() => visibleBuckets.flatMap((b) => b.items), [visibleBuckets]);

  // Breakdown view: per month (buckets) or per day (running balance, like a
  // cash-flow sheet). The daily view starts from the bank balance of the
  // active currency, when known.
  const [view, setView] = useState<'months' | 'days'>('months');
  const openingBalance = useMemo(
    () => data?.balances.totals.find((b) => b.currency === activeCurrency) ?? null,
    [data, activeCurrency],
  );
  const days = useMemo(
    () => buildCashflowDays(allItems, openingBalance?.balance ?? null),
    [allItems, openingBalance],
  );
  const dayLabel = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale === 'ro' ? 'ro-RO' : 'en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
    return (date: string) => fmt.format(new Date(`${date}T00:00:00Z`));
  }, [locale]);
  const upcomingPayments = allItems.filter((i) => i.direction === 'out');
  const upcomingReceipts = allItems.filter((i) => i.direction === 'in');

  const excludedCount =
    (data?.excluded.quarterlyAnnualSubscriptions ?? 0) + (data?.excluded.usageSubscriptions ?? 0);

  async function handleSubmit(input: ScheduledPaymentInput) {
    if (modal?.mode === 'edit') {
      await updateMutation.mutateAsync({ id: modal.row.id, input });
    } else {
      await createMutation.mutateAsync(input);
    }
    setModal(null);
  }

  async function handleDelete() {
    if (!deleting) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(deleting.id);
      setDeleting(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : t('common.error'));
    }
  }

  function sourceLabel(item: CashflowItem): string {
    return t(`cashflow.source.${item.source}`);
  }

  function renderItemActions(item: CashflowItem) {
    if (!canManage || item.source !== 'planned' || !item.refId) return null;
    const row = plannedById.get(item.refId);
    if (!row) return null;
    return (
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={() => setModal({ mode: 'edit', row })}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          aria-label={t('common.edit')}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => {
            setDeleteError(null);
            setDeleting(row);
          }}
          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
          aria-label={t('common.delete')}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </span>
    );
  }

  /** Short "+3.000 EasyPay · −40 Meta" narrative of a day's movements. */
  function dayNarrative(items: CashflowItem[]): string {
    return items
      .map((i) => `${i.direction === 'in' ? '+' : '−'}${fmtAmount(i.amount, i.currency)} ${i.name}`)
      .join(' · ');
  }

  function renderDaysTable() {
    if (days.length === 0) {
      return <p className="px-5 py-4 text-sm text-gray-500">{t('cashflow.days.empty')}</p>;
    }
    const hasBalance = openingBalance !== null;
    return (
      <div className="overflow-x-auto">
        {openingBalance && (
          <p className="px-5 py-2 text-xs text-gray-600">
            {t('cashflow.days.opening')}:{' '}
            <span className="font-semibold text-gray-900">
              {fmtAmount(openingBalance.balance, openingBalance.currency)}
            </span>
            {openingBalance.asOf &&
              ` · ${t('cashflow.balances.asOf', { date: fmtDate(openingBalance.asOf) })}`}
          </p>
        )}
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-5 py-2 text-left font-medium">{t('cashflow.days.date')}</th>
              <th className="px-3 py-2 text-right font-medium">{t('cashflow.days.inflow')}</th>
              <th className="px-3 py-2 text-right font-medium">{t('cashflow.days.outflow')}</th>
              <th className="px-3 py-2 text-right font-medium">{t('cashflow.net')}</th>
              {hasBalance && (
                <th className="px-3 py-2 text-right font-medium">{t('cashflow.days.balance')}</th>
              )}
              <th className="px-5 py-2 text-left font-medium">{t('cashflow.days.what')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {days.map((day) => (
              <tr key={day.date} className="align-top hover:bg-gray-50">
                <td className="whitespace-nowrap px-5 py-2 font-medium capitalize text-gray-900">
                  {dayLabel(day.date)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-emerald-600">
                  {day.inflow > 0 ? `+${fmtAmount(day.inflow, activeCurrency)}` : ''}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right text-red-600">
                  {day.outflow > 0 ? `−${fmtAmount(day.outflow, activeCurrency)}` : ''}
                </td>
                <td
                  className={`whitespace-nowrap px-3 py-2 text-right font-semibold ${
                    day.net >= 0 ? 'text-emerald-700' : 'text-red-700'
                  }`}
                >
                  {fmtAmount(day.net, activeCurrency)}
                </td>
                {hasBalance && (
                  <td
                    className={`whitespace-nowrap px-3 py-2 text-right font-semibold ${
                      (day.balance ?? 0) >= 0 ? 'text-gray-900' : 'text-red-700'
                    }`}
                  >
                    {day.balance !== null ? fmtAmount(day.balance, activeCurrency) : '—'}
                  </td>
                )}
                <td className="min-w-[16rem] px-5 py-2 text-xs text-gray-600">
                  {dayNarrative(day.items)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderItemsList(items: CashflowItem[], emptyKey: string) {
    if (items.length === 0) {
      return <p className="py-4 text-sm text-gray-500">{t(emptyKey)}</p>;
    }
    return (
      <ul className="divide-y divide-gray-100">
        {items.map((item) => (
          <li key={item.id} className="flex items-center justify-between gap-3 py-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-gray-900">
                {item.name}
                {item.isOverdue && (
                  <span
                    className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${
                      item.source === 'planned'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {t(item.source === 'planned' ? 'cashflow.unconfirmed' : 'cashflow.overdue')}
                  </span>
                )}
              </p>
              <p className="text-xs text-gray-500">
                {fmtDate(item.date)} · {sourceLabel(item)}
              </p>
            </div>
            {/* Phones: the amount on top, its edit/delete buttons under it. */}
            <div className="flex shrink-0 flex-col items-end gap-0.5 sm:flex-row sm:items-center sm:gap-2">
              <span
                className={`whitespace-nowrap text-sm font-semibold ${
                  item.direction === 'in' ? 'text-emerald-600' : 'text-red-600'
                }`}
              >
                {item.direction === 'in' ? '+' : '−'}
                {fmtAmount(item.amount, item.currency)}
              </span>
              {renderItemActions(item)}
            </div>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{t('cashflow.title')}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <CompanyMultiSelect
            value={companyIds}
            onChange={setCompanyIds}
            className="w-full sm:w-52"
          />
          {/* Horizon: 1 / 2 / 3 / 6 months, segmented. */}
          <div
            className="inline-flex rounded-md border border-gray-300 p-0.5"
            role="group"
            aria-label={t('cashflow.horizon')}
          >
            {HORIZONS.map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => setMonths(h)}
                className={`rounded px-3 py-1.5 text-sm font-medium ${
                  months === h ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t('cashflow.monthsShort', { count: h })}
              </button>
            ))}
          </div>
          {/* Currency: the whole page (chart, table, lists) follows it. */}
          {currencies.length > 1 && (
            <div
              className="inline-flex rounded-md border border-gray-300 p-0.5"
              role="group"
              aria-label={t('cashflow.currency')}
            >
              {currencies.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCurrency(c)}
                  className={`rounded px-3 py-1.5 text-sm font-medium ${
                    c === activeCurrency
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
          {canManage && (
            <button
              type="button"
              onClick={() => setModal({ mode: 'create' })}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              <Plus className="h-4 w-4" />
              {t('cashflow.addPlanned')}
            </button>
          )}
        </div>
      </div>

      {projection.isError && <p className="mt-6 text-sm text-red-600">{t('cashflow.loadError')}</p>}

      {projection.isLoading && (
        <div className="mt-6 space-y-4">
          <SkeletonChart />
          <SkeletonTable />
        </div>
      )}

      {data && (
        <FadeIn>
          {excludedCount > 0 && (
            <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {t('cashflow.excludedNote', {
                quarterlyAnnual: data.excluded.quarterlyAnnualSubscriptions,
                usage: data.excluded.usageSubscriptions,
              })}
            </p>
          )}

          {/* Money currently in the bank accounts (statement / manual). */}
          <BankBalancesCard balances={data.balances} canEdit={canEditBalances} />

          {/* Per-month inflow/outflow chart, one currency at a time (no FX). */}
          <section className="mt-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-gray-900">{t('cashflow.chartTitle')}</h2>
              <div className="flex flex-wrap items-center gap-3">
                {(() => {
                  const total = data.balances.totals.find((b) => b.currency === activeCurrency);
                  return total ? (
                    <span className="text-xs text-gray-600">
                      {t('cashflow.balances.current')}:{' '}
                      <span className="font-semibold text-gray-900">
                        {fmtAmount(total.balance, total.currency)}
                      </span>
                    </span>
                  ) : null;
                })()}
              </div>
            </div>
            <div className="mt-4">
              <IncomeExpenseBarChart
                data={chartData}
                formatValue={(v) => fmtAmount(v, activeCurrency)}
              />
            </div>
          </section>

          {/* Breakdown: monthly table with expandable item rows, or day by day. */}
          <section className="mt-6 rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-5 py-3">
              <h2 className="text-sm font-semibold text-gray-900">
                {t('cashflow.monthlyBreakdown')}
              </h2>
              <div className="inline-flex rounded-md border border-gray-300 p-0.5" role="group">
                {(['months', 'days'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setView(v)}
                    className={`rounded px-2.5 py-1 text-xs font-medium ${
                      view === v ? 'bg-gray-800 text-white' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {t(`cashflow.view.${v}`)}
                  </button>
                ))}
              </div>
            </div>
            {view === 'days' && renderDaysTable()}
            <div className={view === 'days' ? 'hidden' : 'divide-y divide-gray-100'}>
              {visibleBuckets.map((bucket, index) => {
                const open = isExpanded(bucket.month, index);
                return (
                  <div key={bucket.month}>
                    <button
                      type="button"
                      onClick={() => toggleExpanded(bucket.month, index)}
                      className="flex w-full flex-wrap items-center justify-between gap-2 px-5 py-3 text-left hover:bg-gray-50"
                    >
                      <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-900">
                        {open ? (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        )}
                        <span className="capitalize">{monthLabel(bucket.month)}</span>
                        <span className="text-xs font-normal text-gray-500">
                          ({bucket.items.length})
                        </span>
                      </span>
                      <span className="flex flex-wrap items-center gap-3 text-xs">
                        {bucket.byCurrency.length === 0 && (
                          <span className="text-gray-400">{t('cashflow.noMovements')}</span>
                        )}
                        {bucket.byCurrency.map((ct) => (
                          <span key={ct.currency} className="inline-flex items-center gap-1.5">
                            <span className="text-emerald-600">
                              +{fmtAmount(ct.inflow, ct.currency)}
                            </span>
                            <span className="text-red-600">
                              −{fmtAmount(ct.outflow, ct.currency)}
                            </span>
                            <span
                              className={`font-semibold ${
                                ct.net >= 0 ? 'text-emerald-700' : 'text-red-700'
                              }`}
                            >
                              {t('cashflow.net')}: {fmtAmount(ct.net, ct.currency)}
                            </span>
                          </span>
                        ))}
                        {/* Estimated end-of-month balance (bank balance + cumulative net). */}
                        {(bucket.projectedBalance ?? [])
                          .filter((pb) => pb.balance !== null)
                          .map((pb) => (
                            <span
                              key={`pb-${pb.currency}`}
                              className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-gray-700"
                            >
                              {t('cashflow.projectedBalance')}:{' '}
                              <span
                                className={`font-semibold ${
                                  (pb.balance ?? 0) >= 0 ? 'text-gray-900' : 'text-red-700'
                                }`}
                              >
                                {fmtAmount(pb.balance as number, pb.currency)}
                              </span>
                            </span>
                          ))}
                      </span>
                    </button>
                    {open && bucket.items.length > 0 && (
                      <div className="px-5 pb-3 pl-11">
                        {renderItemsList(bucket.items, 'cashflow.noMovements')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Upcoming payments / receipts, side by side. */}
          {/* grid-cols-1 + min-w-0: the rows' amount + action buttons used to
              size the column and push the page 84px past a phone's edge. */}
          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <section className="min-w-0 rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
              <h2 className="text-sm font-semibold text-gray-900">
                {t('cashflow.upcomingPayments')}
              </h2>
              {renderItemsList(upcomingPayments, 'cashflow.noPayments')}
            </section>
            <section className="min-w-0 rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
              <h2 className="text-sm font-semibold text-gray-900">
                {t('cashflow.upcomingReceipts')}
              </h2>
              {renderItemsList(upcomingReceipts, 'cashflow.noReceipts')}
            </section>
          </div>
        </FadeIn>
      )}

      {modal && (
        <ScheduledPaymentFormModal
          title={modal.mode === 'edit' ? t('cashflow.editPlanned') : t('cashflow.addPlanned')}
          initial={modal.mode === 'edit' ? modal.row : undefined}
          submitting={createMutation.isPending || updateMutation.isPending}
          onClose={() => setModal(null)}
          onSubmit={handleSubmit}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={t('cashflow.deleteTitle')}
          message={t('cashflow.deleteMessage', { name: deleting.name })}
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          danger
          busy={deleteMutation.isPending}
          error={deleteError}
          onConfirm={handleDelete}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// "Money in accounts" card: per-currency totals from the freshest balance per
// account (newest statement closingBalance vs manual override — the backend
// picks the winner), with expandable per-account rows and inline manual edit.
// ---------------------------------------------------------------------------

function BankBalancesCard({ balances, canEdit }: { balances: BankBalances; canEdit: boolean }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const updateMutation = useUpdateBankAccount();

  async function save(account: AccountBalance, value: number | null) {
    setError(null);
    try {
      await updateMutation.mutateAsync({ id: account.id, input: { manualBalance: value } });
      setEditingId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    }
  }

  function startEdit(account: AccountBalance) {
    setError(null);
    setDraft(account.manualBalance !== null ? String(account.manualBalance) : '');
    setEditingId(account.id);
  }

  return (
    <section className="mt-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Landmark className="text-brand-600" size={18} />
          <h2 className="text-sm font-semibold text-gray-900">{t('cashflow.balances.title')}</h2>
        </div>
        {balances.accounts.length > 0 && (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline"
          >
            {open ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            {t('cashflow.balances.accounts', { count: balances.accounts.length })}
          </button>
        )}
      </div>

      {balances.accounts.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">{t('cashflow.balances.empty')}</p>
      ) : balances.totals.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">{t('cashflow.balances.unknown')}</p>
      ) : (
        <div className="mt-3 flex flex-wrap items-baseline gap-x-8 gap-y-2">
          {balances.totals.map((total) => (
            <div key={total.currency}>
              <p
                className={`text-2xl font-bold ${
                  total.balance >= 0 ? 'text-gray-900' : 'text-red-600'
                }`}
              >
                {fmtAmount(total.balance, total.currency)}
              </p>
              {total.asOf && (
                <p className="text-xs text-gray-500">
                  {t('cashflow.balances.asOf', { date: fmtDate(total.asOf) })}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      {open && balances.accounts.length > 0 && (
        <ul className="mt-4 divide-y divide-gray-100 border-t border-gray-100">
          {balances.accounts.map((account) => (
            <li key={account.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">
                  {account.label ?? account.bankName}
                  <span className="ml-2 text-xs font-normal text-gray-400">{account.currency}</span>
                </p>
                <p className="font-mono text-xs text-gray-500">{account.iban}</p>
              </div>
              <div className="flex items-center gap-2">
                {editingId === account.id ? (
                  <span className="inline-flex items-center gap-1.5">
                    <input
                      type="number"
                      step="0.01"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      autoFocus
                      className="w-32 rounded border border-gray-300 px-2 py-1 text-right text-sm"
                      aria-label={t('cashflow.balances.editLabel')}
                    />
                    <button
                      type="button"
                      disabled={
                        updateMutation.isPending ||
                        draft.trim() === '' ||
                        Number.isNaN(Number(draft))
                      }
                      onClick={() => void save(account, Number(draft))}
                      className="rounded-md bg-brand-600 px-2 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                    >
                      {t('common.save')}
                    </button>
                    {account.manualBalance !== null && (
                      <button
                        type="button"
                        disabled={updateMutation.isPending}
                        onClick={() => void save(account, null)}
                        className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        title={t('cashflow.balances.clearHint')}
                      >
                        {t('cashflow.balances.clear')}
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={updateMutation.isPending}
                      onClick={() => setEditingId(null)}
                      className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {t('common.cancel')}
                    </button>
                  </span>
                ) : (
                  <>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">
                        {account.balance !== null
                          ? fmtAmount(account.balance, account.currency)
                          : '—'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {account.balance !== null && account.source ? (
                          <>
                            {t(`cashflow.balances.source.${account.source}`)}
                            {account.asOf &&
                              ` · ${t('cashflow.balances.asOf', { date: fmtDate(account.asOf) })}`}
                          </>
                        ) : (
                          t('cashflow.balances.unknownAccount')
                        )}
                      </p>
                    </div>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => startEdit(account)}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        aria-label={t('cashflow.balances.editLabel')}
                        title={t('cashflow.balances.editLabel')}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
